import * as React from 'react';
import { Collapse, Grid, GridOwnProps, IconButton, InputAdornment, TextField } from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import SpinnerButton from '../../../components/SpinnerButton';
import { AutoCloseEnumField, AutocompleteEnumField, DateField, TextF } from '../../../components/fields';
import debounce from 'lodash.debounce';

export interface Section {
    type: 'section';
    name: string;
    fields: FilterField[];
    width?: number;
}

export interface BaseField {
    name: string;
    disabled?: boolean;
    width?: number;
}
interface SearchField extends BaseField {
    type: 'search';
    name: 'search';
    placeholder: string;
}

interface TextFilterField extends BaseField {
    type: 'string';
    placeholder: string;
}

interface EnumFilterField extends BaseField {
    type: 'enum';
    forceSingle?: boolean,
    items: (string | [string, string])[];
    emptyLabel: string;
}

interface AutocompleteFilterField extends BaseField {
    type: 'autocomplete';
    items: string[];
    emptyLabel: string;
}

interface DateField extends BaseField {
    type: 'date';
    placeholder: string;
}

const MaybeCollapse = ({ collapsable, open, children }: { collapsable:boolean, open: boolean, children: React.ReactNode }): JSX.Element => {
    return collapsable
        ? <Collapse in={open} timeout='auto'>{children}</Collapse>
        : <>{children}</>
}

export type FilterField = TextFilterField | EnumFilterField | SearchField | AutocompleteFilterField | DateField | Section;

export interface FilterValue {
    [k: string]: (string | string[]);
}

export interface FiltersPanelProps extends GridOwnProps {
    fields: FilterField[];
    defaultValue: FilterValue;
    value?: FilterValue;
    isBusy?:boolean,
    collapsable?:boolean;
    gridPadding?:string;
    onChange?: (value: FilterValue) => void;
    onApply?: () => void;
}

const SectionFilter = ({ fields, value: extValue, defaultValue, isBusy, collapsable=false, gridPadding,
            onChange, onApply, ...props }: FiltersPanelProps): JSX.Element => {
    const [value, setValue] = React.useState<FilterValue>(extValue || defaultValue);
    const [selectedSection, setSelectedSection] = React.useState<string | null>(fields.find(f => f.type === 'section')?.name || null);

    React.useEffect(() => {
        extValue && setValue(extValue);
    }, [extValue]);

    const [open, setOpen] = React.useState(fields.filter(f => f.type == 'search').length > 0);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const fetch = React.useCallback(debounce((value: FilterValue) => {
        onChange && onChange(value);
    }, 1000), [value]);

    const onStringChange = React.useCallback((label: string, newValue: string | null) => {
        setValue({ ...value, [label]: newValue || '' });
        fetch({ ...value, [label]: newValue || '' });
    }, [fetch, value]);

    const onEnumChange = React.useCallback((label: string, newValue: string[] | null) => {
        if (!value[label].includes('all') && (newValue || []).includes('all')) {
            newValue = ['all'];
        }
        else {
            newValue = newValue?.filter(v => v != 'all') || [];
        }
        setValue({ ...value, [label]: newValue || [] });
        fetch({ ...value, [label]: newValue || [] });
    }, [fetch, value]);

    const onEnumSingleChange = React.useCallback((label: string, newValue: string[] | null) => {
        let workingValue = (newValue && ((typeof newValue == 'string') ? [newValue] : newValue)) || [];
        if (!value[label].includes('all') && workingValue.includes('all')) {
            workingValue = ['all'];
        }
        setValue({ ...value, [label]: workingValue });
        fetch({ ...value, [label]: workingValue });
    }, [fetch, value]);

    const onDateChange = React.useCallback((label: string, newValue: Date | null) => {
        setValue({ ...value, [label]: newValue?.toISOString() || '' });
        fetch({ ...value, [label]: newValue?.toISOString() || '' });
    }, [fetch, value]);

    const onSearchChange = React.useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        setValue({ ...value, search: event.target.value });
        fetch({ ...value, search: event.target.value });
    }, [fetch, value]);

    const dateValue = React.useMemo(() => (
        Object.fromEntries(
            fields.filter(f => f.type == 'date')
                .map(f => [f.name, new Date(value[f.name] as string)])
        )
    ), [fields, value]);

    const enumLabels = React.useMemo(() => {
        function getEnumFields(fields: FilterField[]): EnumFilterField[] {
            return fields.reduce((acc, f) => {
                if (f.type === 'enum') {
                    acc.push(f);
                } else if (f.type === 'section') {
                    acc.push(...getEnumFields(f.fields));
                }
                return acc;
            }, [] as EnumFilterField[]);
        }

        return Object.fromEntries(getEnumFields(fields).map(f => (
            [f.name, Object.fromEntries(f.items.map(item => (
                item instanceof Array
                    ? item
                    : [item, item]
            )))]
        )))
    }, [fields])

    const onClearSearchClick = React.useCallback((event: React.MouseEvent<HTMLElement>) => {
        let e: HTMLElement | null = event.currentTarget;
        while (e && !e.attributes.getNamedItem('x-field')) {
            e = e.parentElement;
        }
        const idAttr = e && e.attributes.getNamedItem('x-field');
        if (idAttr) {
            setValue({ ...value, [idAttr.value]: '' });
            fetch({ ...value, [idAttr.value]: '' });
        }
    }, [fetch, value]);

    const onClearClick = React.useCallback(() => {
        const empty = Object.fromEntries(fields.map(f => [
            f.name,
            f.type == 'enum' ? ['all']
                : f.type == 'autocomplete' ? []
                    : ''
        ]));
        setValue(empty);
        onChange && onChange(empty);
    }, [fields, onChange]);

    const toggleOpen = React.useCallback(() => {
        setOpen(!open);
    }, [open]);

    return <Grid container padding={gridPadding||'8px 32px 0 32px'} sx={{
        '.MuiInputBase-root': {
            minHeight: '41px' // for chips
        },
    }} {...props}>
        {fields
            .filter<Section>((f => f.type == 'section' && f.name == 'top_bar') as (f: FilterField) => f is Section)
            .map((f, i) => (
                <Grid item xs={12} sx={{ display: 'flex', gap:2 }} key={f.name}>
                    {f.fields.map((sf: FilterField, si: number) => (
                        <Grid item key={`${i}-${si}`} xs={sf.width || 4} >
                            {renderField(sf, value, onEnumChange, onEnumSingleChange, onStringChange, onDateChange, onSearchChange, onClearSearchClick, enumLabels, dateValue)}
                        </Grid>
                    ))}
                    <IconButton onClick={toggleOpen} title={open ? 'Search Only' : 'Show Filters'}>{open ? <ExpandLess /> : <ExpandMore />}</IconButton>
                </Grid>

            ))}

        <Grid item key={`gridspacer`} xs={12}>
        <MaybeCollapse collapsable={collapsable} open={open}>
            <Grid container spacing={2} sx={{
                alignItems: 'flex-end',
                '.MuiInputBase-root': {
                    minHeight: '41px' // for chips
                },
                paddingTop:0.5
            }} {...props}>
                {(fields
                    .filter(f => f.type === 'section' && f.name !== 'top_bar') as Section[])
                    .map((f: Section, i) => (
                        <Grid item key={f.name} >
                            <SpinnerButton
                                onClick={() => setSelectedSection(f.name === selectedSection ? null : f.name)}
                                showSpinner={isBusy || false}
                                variant={f.name === selectedSection ? 'contained' : 'outlined'}
                            >
                                {f.name}
                            </SpinnerButton>
                        </Grid>
                    ))}
                {fields
                    .filter(f => f.name !== 'top_bar' && (f.type !== 'section' || !selectedSection || f.name === selectedSection))
                    .map((f, i) => {
                        if (f.type === 'section') {
                            return selectedSection === f.name && (
                                <Grid item xs={12} key={f.name}>
                                    <Grid container spacing={2}>
                                        {f.fields.map((sf: FilterField, si: number) => (
                                            <Grid item key={`${i}-${si}`} xs={sf.width || 4}>
                                                {renderField(sf, value, onEnumChange, onEnumSingleChange, onStringChange, onDateChange, onSearchChange, onClearSearchClick, enumLabels, dateValue)}
                                            </Grid>
                                        ))}
                                    </Grid>
                                </Grid>
                            );
                        }
                        return (
                            <Grid item key={`${i}`} xs={f.width || 4}>
                                {renderField(f, value, onEnumChange, onEnumSingleChange, onStringChange, onDateChange, onSearchChange, onClearSearchClick, enumLabels, dateValue)}
                            </Grid>
                        );
                    })}
            </Grid>
            <Grid container justifyContent="flex-end">
                <SpinnerButton onClick={onClearClick} showSpinner={isBusy || false} >Clear</SpinnerButton>
            </Grid>
        </MaybeCollapse>
        </Grid>
    </Grid>
};

const renderField = (f: FilterField, value: FilterValue, 
    onEnumChange: any, 
    onEnumSingleChange: any,
    onStringChange: any,
    onDateChange: any,
    onSearchChange: any,
    onClearSearchClick: any,
    enumLabels: any,
    dateValue: any) => {
    switch (f.type) {
        case 'search':
            return <TextField
                name={f.name}
                value={value[f.name]}
                onChange={onSearchChange}
                variant='standard'
                hiddenLabel
                placeholder={f.placeholder}
                fullWidth
                disabled={f.disabled}
                InputProps={{
                    endAdornment: value.search ? <InputAdornment position='end' >
                        <IconButton x-field={f.name} onClick={onClearSearchClick}>
                            <ClearIcon />
                        </IconButton>
                    </InputAdornment> : undefined
                }}
            />
        case 'autocomplete':
            return <AutocompleteEnumField
                name={f.name}
                value={value[f.name] as string[]}
                options={f.items}
                hiddenLabel
                onChange={onEnumChange}
            />;
        case 'enum':
            return <AutoCloseEnumField multiple={!f.forceSingle} name={f.name} value={(value[f.name] as string[]) || ['all']}
                options={[['all', f.emptyLabel], ...(f.items || [])]}
                valueToClose='all'
                hiddenLabel
                disabled={f.disabled}
                onChange={f.forceSingle ? onEnumSingleChange : onEnumChange}
                renderValue={(selected: any) => {
                    if(typeof selected == 'string') // TODO: INVESTIGATE WHY SOMETIMES STRING AND SOMETIMES AN ARRAY
                        return f.emptyLabel || selected;
                    return selected.includes('all')
                        ? f.emptyLabel
                        : selected.map((v: any) => enumLabels[f.name][v]).join(', ')
                }
                } />;
        case 'string':
            return <TextF
                name={f.name} label={f.placeholder} value={value[f.name] as string}
                onChange={onStringChange}
                variant='standard'
                hiddenLabel
                placeholder={f.placeholder}
                fullWidth
                disabled={f.disabled}
                InputProps={{
                    endAdornment: value[f.name] ? <InputAdornment position='end' >
                        <IconButton x-field={f.name} onClick={onClearSearchClick}>
                            <ClearIcon />
                        </IconButton>
                    </InputAdornment> : undefined
                }}
            />;
        case 'date':
            return <DateField
                name={f.name}
                label={f.placeholder}
                value={dateValue[f.name]}
                onChange={onDateChange}
            />;
        default:
            return null;
    }
}

export default SectionFilter;